import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { es } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import Tooltip from '@mui/material/Tooltip';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Popover,
  Button,
  Box,
  IconButton,
  CircularProgress,
  TextField,
} from '@mui/material';
import { EventAvailable, EventBusy, Delete } from '@mui/icons-material';
import { db } from './firebaseConfig'; // Asegurate de tener configurado Firebase
import { collection, getDocs, setDoc, deleteDoc, doc } from 'firebase/firestore';

// Loader Component
const Loader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress size={50} />
    <Typography variant="body1" sx={{ marginLeft: '10px' }}>Cargando...</Typography>
  </Box>
);

const ScheduleTable = () => {
  const today = new Date();
  const [currentDay, setCurrentDay] = useState(today);
  const [reservations, setReservations] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedCourt, setSelectedCourt] = useState('');
  const [userName, setUserName] = useState('');
  const [reserving, setReserving] = useState(false);


  const times = Array.from({ length: 33 }, (_, i) => {
    const hour = Math.floor(i / 2) + 8;
    const minutes = i % 2 === 0 ? '00' : '30';
    return `${hour}:${minutes}`;
  });

  useEffect(() => {
    const fetchReservations = async () => {
      const querySnapshot = await getDocs(collection(db, 'reservations'));
      const resData = {};
      querySnapshot.forEach((doc) => {
        resData[doc.id] = doc.data();
      });
      setReservations(resData);
    };

    fetchReservations();
  }, []);

  const handleDateChange = async (date) => {
    setLoading(true);
    setCurrentDay(date);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setLoading(false);
  };

  const handleOpenPopover = (event, time, court) => {
    setAnchorEl(event.currentTarget);
    setSelectedTime(time);
    setSelectedCourt(court);
    setUserName('');
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleReservation = async (type) => {
    if (!userName && type !== 'disponible') {
      toast.error('Por favor, ingresa un nombre para la reserva.');
      return;
    }
  
    setReserving(true); // ⬅️ Mostrar loader
  
    try {
      const keyBase = `${selectedTime}_${selectedCourt}`;
      const newReservation = {
        type,
        name: userName || 'Anónimo',
      };
  
      if (type === 'reservada_fija') {
        const weeks = 8;
        const newReservations = {};
        for (let i = 0; i < weeks; i++) {
          const newDate = new Date(currentDay);
          newDate.setDate(currentDay.getDate() + i * 7);
          const formattedDate = format(newDate, 'dd-MM-yyyy');
          const key = `${formattedDate}_${keyBase}`;
          await setDoc(doc(db, 'reservations', key), newReservation);
          newReservations[key] = newReservation;
        }
        setReservations((prev) => ({ ...prev, ...newReservations }));
        toast.success('Reserva fija realizada para las próximas 8 semanas.');
      } else if (type === 'disponible') {
        const key = `${format(currentDay, 'dd-MM-yyyy')}_${keyBase}`;
        await deleteDoc(doc(db, 'reservations', key));
        const updated = { ...reservations };
        delete updated[key];
        setReservations(updated);
        toast.success('Reserva liberada correctamente.');
      } else {
        const key = `${format(currentDay, 'dd-MM-yyyy')}_${keyBase}`;
        await setDoc(doc(db, 'reservations', key), newReservation);
        setReservations((prev) => ({
          ...prev,
          [key]: newReservation,
        }));
        toast.success('Reserva realizada correctamente.');
      }
  
      handleClosePopover();
    } catch (error) {
      toast.error('Ocurrió un error al procesar la reserva.');
      console.error(error);
    } finally {
      setReserving(false); // ⬅️ Ocultar loader
    }
  };
  
  if (reserving) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={50} />
        <Typography variant="h6" sx={{ ml: 2, color: '#fff' }}>Reservando...</Typography>
      </Box>
    );
  }
  
  

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, padding: '0', backgroundColor: '#2f2f2f', minHeight: '100vh', marginTop: '64px' }}>
      <Box sx={{ flex: '1', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', marginBottom: { xs: '20px', md: '0' } }}>
        <Box 
          sx={{ 
            textAlign: 'center', 
            marginBottom: '20px',
            background: 'linear-gradient(to top, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0))',
            padding: '10px',
            borderRadius: '50%',
            width: '220px',
            height: '220px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <img 
            src="/images/elcampito1.png" 
            alt="Logo" 
            style={{ 
              width:'200px',
              height:'200px',
              borderRadius:'50%',
              objectFit: 'contain'
            }} 
          />
        </Box>

        <Box>
          <Typography variant="h5" align="center" gutterBottom sx={{ color: '#fff' }}>
            Selecciona un día
          </Typography>
          <DatePicker
            selected={currentDay}
            onChange={handleDateChange}
            dateFormat="dd/MM/yyyy"
            inline
            todayButton="Hoy"
            locale={es}
          />
        </Box>
      </Box>

      <Box sx={{ flex: '3', padding: '20px' }}>
        {loading ? (
          <Loader />
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', marginBottom: '20px' }}>
              <Box sx={{ textAlign: 'center', color: '#fff' }}>
                <IconButton sx={{ color: '#ffc107' }}><EventAvailable /></IconButton>
                <Typography variant="body2">Alquilar Eventual</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', color: '#fff' }}>
                <IconButton sx={{ color: '#155724' }}><EventBusy /></IconButton>
                <Typography variant="body2">Alquilar Fijo</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', color: '#fff' }}>
                <IconButton sx={{ color: '#dc3545' }}><Delete /></IconButton>
                <Typography variant="body2">Eliminar Reserva</Typography>
              </Box>
            </Box>

            <Typography
              variant="h5"
              align="center"
              gutterBottom
              sx={{
                fontFamily: 'Georgia, serif',
                fontWeight: 'bold',
                color: '#fff',
                margin: '0 20px',
              }}
            >
              Horarios de Canchas - {format(currentDay, 'EEEE dd/MM/yyyy', { locale: es })}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                maxWidth: { xs: '100%', md: '700px' },
                margin: '0 auto',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#343a40', color: '#fff', textAlign: 'center', padding: '2px', border: '1px solid black' }}>
                      Horario
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#343a40', color: '#fff', textAlign: 'center', padding: '2px', border: '1px solid black' }}>
                      Cancha 1
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#343a40', color: '#fff', textAlign: 'center', padding: '2px', border: '1px solid black' }}>
                      Cancha 2
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {times.map((time) => (
                    <TableRow key={time}>
                      <TableCell sx={{ backgroundColor: '#343a40', color: '#fff', textAlign: 'center', padding: '2px', border: '1px solid black' }}>
                        {time}
                      </TableCell>

                      {[1, 2].map((court) => {
                        const key = `${format(currentDay, 'dd-MM-yyyy')}_${time}_${court}`;
                        const reservation = reservations[key];

                        return (
                          <Tooltip
                            key={court}
                            title="Haga click y reserve"
                            placement="top"
                            arrow
                          >
                            <TableCell
                              sx={{
                                backgroundColor: reservation
                                  ? (reservation.type === 'reservada_eventual' ? '#ffc107' : '#155724')
                                  : '#28a745',
                                color: '#000',
                                textAlign: 'center',
                                padding: '2px',
                                border: '1px solid black',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#005f00',
                                },
                              }}
                              onClick={(e) => handleOpenPopover(e, time, court.toString())}
                            >
                              {reservation
                                ? `${reservation.name} (${reservation.type === 'reservada_eventual' ? 'Eventual' : 'Fijo'})`
                                : 'Disponible'}
                            </TableCell>
                          </Tooltip>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClosePopover}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{
                sx: {
                  padding: 2,
                  borderRadius: 2,
                  width: { xs: '90vw', sm: '350px' },
                  backgroundColor: '#f4f4f4',
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Nombre"
                  variant="outlined"
                  fullWidth
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                />
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                  <Button
                    onClick={() => handleReservation('reservada_eventual')}
                    variant="contained"
                    sx={{ backgroundColor: '#ffc107', color: '#000', '&:hover': { backgroundColor: '#e0a800' } }}
                    fullWidth
                  >
                    Eventual
                  </Button>
                  <Button
                    onClick={() => handleReservation('reservada_fija')}
                    variant="contained"
                    sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
                    fullWidth
                  >
                    Fijo
                  </Button>
                  <Button
                    onClick={() => handleReservation('disponible')}
                    variant="contained"
                    color="error"
                    fullWidth
                  >
                    Liberar
                  </Button>
                </Box>
              </Box>
            </Popover>
          </>
        )}
      </Box>


      <ToastContainer position="top-center" autoClose={3000} />
    </Box>
  );
};

export default ScheduleTable;
